import { useQuery } from '@apollo/client';
import { useRoute } from '@react-navigation/native';
import { BarChart } from 'react-native-gifted-charts';

import { PracticeOutcome } from '@oui/app-core/src/components/PracticeOutcome/PracticeOutcome';
import { RoundedSection } from '@oui/app-core/src/components/RoundedSection';
import { Heading, Label } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { useI18n } from '@oui/app-core/src/lib/i18n';
import { STRESS_BREATHING_ROUND_COLORS } from '@oui/app-core/src/ptsdTheme';
import { useTheme } from '@oui/app-core/src/styles';
import { parseGQLDate } from '@oui/lib/src/gqlDate';
import { graphql } from '@oui/lib/src/graphql/tada';
import { times } from '@oui/lib/src/times';

import { StackScreenProps } from '../../types/navigation';

export const StressBreathingShowQuery = graphql(`
  query StressBreathingShow(
    $practiceID: UUID!
    $hyperventilation: Boolean!
    $breathHolding: Boolean!
  ) {
    user {
      ID
      role {
        ID
        breathHoldingWidget @include(if: $breathHolding) {
          ID
          stressBreathingExerciseByID(practiceID: $practiceID) {
            practiceID
            practiceValues {
              date
              ratings {
                type
                value
              }
            }
            stressBreathingExercise {
              ...Exercise
            }
          }
        }
        hyperventilationWidget @include(if: $hyperventilation) {
          ID
          stressBreathingExerciseByID(practiceID: $practiceID) {
            practiceID
            practiceValues {
              date
              ratings {
                type
                value
              }
            }
            stressBreathingExercise {
              ...Exercise
            }
          }
        }
      }
    }
  }
  fragment Exercise on StressBreathingExercise {
    isComplete
    rounds {
      isComplete
      distressRating
    }
  }
`);

/**
 * Displays data for an individual stress breathing practice.
 */
export const StressBreathingShow = () => {
  const route = useRoute<StackScreenProps<'StressBreathingShow'>['route']>();
  const { theme } = useTheme();
  const { formatDate } = useI18n();
  const type = route.params?.type ?? 'holding';
  const { data } = useQuery(StressBreathingShowQuery, {
    variables: {
      practiceID: route.params.practiceID,
      breathHolding: type === 'holding',
      hyperventilation: type === 'hyperventilation',
    },
  });

  const entry =
    data?.user?.role?.breathHoldingWidget?.stressBreathingExerciseByID ??
    data?.user?.role?.hyperventilationWidget?.stressBreathingExerciseByID;

  const distressOutcome = !!entry?.stressBreathingExercise.rounds.every(
    (r) => typeof r.distressRating === 'number' && r.distressRating <= 3,
  );
  const ratingOkOrBetter = !!entry?.practiceValues.ratings.find(
    (r) => r.type === 'RATING_AFTER' && r.value >= 0,
  );

  return (
    <RoundedSection
      testID="StressBreathingShow"
      color="white"
      title={
        entry
          ? formatDate(parseGQLDate(entry.practiceValues.date), {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })
          : ''
      }
      secondaryColor={theme.color.gray800}
      applyHeaderOptions
    >
      {entry ? (
        <View style={{ gap: 20, flexGrow: 1 }}>
          <Heading level={3} text="Outcome" textAlign="center" />
          <PracticeOutcome
            outcomes={[
              { icon: 'distress', text: 'Distress was low each round', value: distressOutcome },
              { icon: 'thumb-up', text: 'Rated ‘Went ok’ or better', value: ratingOkOrBetter },
            ]}
          />
          <View
            style={{
              backgroundColor: 'white',
              borderTopStartRadius: 30,
              borderTopEndRadius: 30,
              marginHorizontal: -20,
              padding: 20,
              flexGrow: 1,
            }}
          >
            <Label small text="Distress rating" />
            <BarChart
              barWidth={50}
              data={times(5, (i) => ({
                value: entry.stressBreathingExercise.rounds[i]?.distressRating ?? 0,
                frontColor: STRESS_BREATHING_ROUND_COLORS[i],
                barBorderTopLeftRadius: 3,
                barBorderTopRightRadius: 3,
              }))}
              disablePress
              disableScroll
              endSpacing={8 / 2} // spacing seems to apply twice, so divide by 2
              initialSpacing={8 / 2} // spacing seems to apply twice, so divide by 2
              labelWidth={8 * 5 + 1 * 4} // width of bars + spacing b/t bars in group
              maxValue={10}
              spacing={12}
              xAxisLabelTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
              xAxisLabelTexts={['1', '2', '3', '4', '5']}
              yAxisLabelTexts={['', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']}
              yAxisLabelWidth={25}
              yAxisTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
            />
            <Label small text="Rounds" textAlign="center" />
          </View>
        </View>
      ) : null}
    </RoundedSection>
  );
};
